import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'https://agents.matix.one';

const PartsCatalog = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [expandedStock, setExpandedStock] = useState({});
  const [stockData, setStockData] = useState({});
  const [stockLoading, setStockLoading] = useState({});
  const [error, setError] = useState(null);

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)', overflow: 'hidden'
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/v1/fsm/erp-products?page=${page}&limit=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        setProducts([]);
      } else {
        setProducts(data.products || []);
        setPagination(data.pagination || {});
      }
    } catch (err) {
      console.error(err);
      setError('Failed to connect to ERP');
    }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const toggleStock = async (productId) => {
    if (expandedStock[productId]) {
      setExpandedStock(prev => ({ ...prev, [productId]: false }));
      return;
    }
    setExpandedStock(prev => ({ ...prev, [productId]: true }));
    if (stockData[productId]) return;

    setStockLoading(prev => ({ ...prev, [productId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/v1/fsm/erp-products/${productId}/stock`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStockData(prev => ({ ...prev, [productId]: data.stock || [] }));
    } catch (err) { console.error(err); }
    finally { setStockLoading(prev => ({ ...prev, [productId]: false })); }
  };

  const getStockColor = (qty) => {
    if (qty <= 0) return '#ef4444';
    if (qty < 5) return '#f59e0b';
    return '#22c55e';
  };

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '24px', color: '#f9fafb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
              {isRTL ? 'كتالوج القطع والمنتجات' : 'Parts & Products Catalog'}
            </h1>
            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '14px' }}>
              {isRTL ? 'تصفح المنتجات والمخزون من MatixERP' : 'Browse products & inventory from MatixERP'}
            </p>
          </div>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isRTL ? 'بحث بالاسم أو الكود...' : 'Search by name or code...'}
              style={{
                padding: '10px 16px', borderRadius: '10px', border: '2px solid rgba(255,255,255,0.15)',
                backgroundColor: 'rgba(26,32,56,0.8)', color: '#f9fafb', fontSize: '14px',
                outline: 'none', minWidth: '250px'
              }}
            />
            <button type="submit" style={{
              backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '10px',
              padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}>
              {isRTL ? 'بحث' : 'Search'}
            </button>
          </form>
        </div>

        {error && (
          <div style={{ ...sectionSt, padding: '20px', textAlign: 'center', marginBottom: '16px', borderColor: 'rgba(239,68,68,0.3)' }}>
            <span style={{ color: '#f87171' }}>{error}</span>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
            {isRTL ? 'جاري التحميل...' : 'Loading products from ERP...'}
          </div>
        ) : products.length === 0 && !error ? (
          <div style={{ ...sectionSt, padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
            <div style={{ color: '#9ca3af', fontSize: '16px' }}>
              {search ? (isRTL ? 'لا توجد نتائج' : 'No products found') : (isRTL ? 'لا توجد منتجات' : 'No products available')}
            </div>
          </div>
        ) : (
          <>
            <div style={sectionSt}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    {[
                      isRTL ? 'الكود' : 'Code',
                      isRTL ? 'الاسم' : 'Name',
                      isRTL ? 'الماركة' : 'Brand',
                      isRTL ? 'الموديل' : 'Model',
                      isRTL ? 'الوحدة' : 'Unit',
                      isRTL ? 'التكلفة' : 'Cost',
                      isRTL ? 'سعر البيع' : 'Price',
                      isRTL ? 'المخزون' : 'Stock',
                      ''
                    ].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: isRTL ? 'right' : 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <React.Fragment key={p.id}>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontFamily: 'monospace', color: '#60a5fa' }}>{p.code}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <div style={{ fontWeight: '600' }}>{isRTL ? (p.name_ar || p.name) : p.name}</div>
                          {p.name_ar && !isRTL && <div style={{ color: '#9ca3af', fontSize: '11px', direction: 'rtl' }}>{p.name_ar}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#d1d5db' }}>{p.brand || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#d1d5db' }}>{p.model || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#d1d5db' }}>{p.unit_name || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#d1d5db' }}>{p.cost_price != null ? p.cost_price.toFixed(2) : '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#d1d5db' }}>{p.selling_price != null ? p.selling_price.toFixed(2) : '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '12px',
                            backgroundColor: getStockColor(p.total_qty) + '20', color: getStockColor(p.total_qty)
                          }}>
                            {p.total_qty != null ? p.total_qty : '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => toggleStock(p.id)} style={{
                            backgroundColor: 'transparent', border: '1px solid rgba(96,165,250,0.3)',
                            color: '#60a5fa', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px'
                          }}>
                            {expandedStock[p.id] ? (isRTL ? 'إخفاء' : 'Hide') : (isRTL ? 'المخازن' : 'Stores')}
                          </button>
                        </td>
                      </tr>
                      {expandedStock[p.id] && (
                        <tr>
                          <td colSpan="9" style={{ padding: '0 16px 12px 40px', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                            {stockLoading[p.id] ? (
                              <div style={{ padding: '12px', color: '#9ca3af', fontSize: '12px' }}>Loading stock...</div>
                            ) : stockData[p.id] && stockData[p.id].length > 0 ? (
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                                <thead>
                                  <tr>
                                    {[isRTL ? 'المخزن' : 'Store', isRTL ? 'الكمية' : 'Qty', isRTL ? 'متوسط التكلفة' : 'Avg Cost', isRTL ? 'الحد الأدنى' : 'Min', isRTL ? 'الحد الأقصى' : 'Max', isRTL ? 'الموقع' : 'Location'].map((h, i) => (
                                      <th key={i} style={{ padding: '6px 10px', textAlign: isRTL ? 'right' : 'left', color: '#9ca3af', fontSize: '11px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {stockData[p.id].map((s, si) => (
                                    <tr key={si}>
                                      <td style={{ padding: '6px 10px', fontSize: '12px' }}>{isRTL ? (s.store_name_ar || s.store_name) : s.store_name}</td>
                                      <td style={{ padding: '6px 10px', fontSize: '12px', fontWeight: '600', color: getStockColor(s.qty) }}>{s.qty}</td>
                                      <td style={{ padding: '6px 10px', fontSize: '12px', color: '#d1d5db' }}>{s.avg_cost.toFixed(2)}</td>
                                      <td style={{ padding: '6px 10px', fontSize: '12px', color: '#d1d5db' }}>{s.min_qty}</td>
                                      <td style={{ padding: '6px 10px', fontSize: '12px', color: '#d1d5db' }}>{s.max_qty}</td>
                                      <td style={{ padding: '6px 10px', fontSize: '12px', color: '#d1d5db' }}>{s.location || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{ padding: '12px', color: '#9ca3af', fontSize: '12px' }}>
                                {isRTL ? 'لا توجد بيانات مخزون' : 'No stock data available'}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'transparent', color: page <= 1 ? '#4b5563' : '#d1d5db', cursor: page <= 1 ? 'default' : 'pointer', fontSize: '13px' }}>
                  {isRTL ? 'السابق' : 'Previous'}
                </button>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                  {isRTL ? `صفحة ${page} من ${pagination.total_pages} (${pagination.total} منتج)` : `Page ${page} of ${pagination.total_pages} (${pagination.total} products)`}
                </span>
                <button disabled={page >= pagination.total_pages} onClick={() => setPage(p => p + 1)}
                  style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'transparent', color: page >= pagination.total_pages ? '#4b5563' : '#d1d5db', cursor: page >= pagination.total_pages ? 'default' : 'pointer', fontSize: '13px' }}>
                  {isRTL ? 'التالي' : 'Next'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default PartsCatalog;
